const API_ROOT = (import.meta.env.VITE_API_ROOT as string | undefined) ?? '/api/v1'
const DEMO_MODE = Boolean(import.meta.env.DEV && !import.meta.env.VITE_API_ROOT)

export type Announcement = { id: string; title: string; content: string; publishedAt: string | null; pinned: boolean }
export type AdminAnnouncement = Announcement & { published: boolean }
export type Dashboard = {
  users: number
  puzzles: number
  scores: number
  feedbackOpen: number
  pendingScores: number
  pendingProfileReviews?: number
}
export type AdminUser = {
  id: string
  email?: string
  nickname: string
  role: string
  /** 有自定义头像（自传或微信头像）的账号才需要盯头像合规。 */
  customAvatar?: boolean
  createdAt: string
}
/**
 * 人工审核队列的一条记录（昵称或头像）。
 * 昵称与头像改成"提交即进队列"之后，玩家改完不会立刻生效，
 * 必须在这里点通过才写回账号。
 */
export type ProfileReview = {
  id: string
  userId: string
  kind: 'NICKNAME' | 'AVATAR'
  status: string
  nickname: string | null
  hasAvatar: boolean
  /** 微信 CDN 头像可以直接用 <img> 显示；本服务暂存的头像走管理员专用接口。 */
  pendingAvatarUrl: string | null
  previousNickname: string | null
  previousAvatarUrl: string | null
  userNickname: string | null
  userEmail: string | null
  createdAt: string | null
  reviewedAt: string | null
  rejectReason: string | null
}
export type Session = {
  accessToken: string
  refreshToken: string
  expiresIn: number
  user: { id: string; email: string; nickname: string; role: string; guest: boolean }
}

// 服务端错误体是 {code,message}；把 message 透出来，后台才能给出可读的提示。
async function request<T>(path: string, init?: RequestInit): Promise<T> {
  if (DEMO_MODE) throw new Error('demo mode')
  const response = await fetch(`${API_ROOT}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) }
  })
  if (!response.ok) {
    let detail = ''
    try { detail = ((await response.json()) as { message?: string }).message ?? '' } catch { /* 保留状态码 */ }
    throw new Error(detail || `请求失败 (HTTP ${response.status})`)
  }
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

const bearer = (token: string) => ({ Authorization: `Bearer ${token}` })

export const api = {
  announcements: () => request<Announcement[]>('/public/announcements'),
  // 服务端 FeedbackRequest 的字段是 content，不是 message。
  feedback: (payload: { category: string; content: string; contact?: string }) =>
    request<{ id: string; status: string }>('/public/feedback', { method: 'POST', body: JSON.stringify(payload) }),
  login: (email: string, password: string) =>
    request<Session>('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  dashboard: (token: string) => request<Dashboard>('/admin/dashboard', { headers: bearer(token) }),
  users: (token: string) => request<AdminUser[]>('/admin/users', { headers: bearer(token) }),
  adminAnnouncements: (token: string) => request<AdminAnnouncement[]>('/admin/announcements', { headers: bearer(token) }),
  createAnnouncement: (token: string, payload: { title: string; content: string; published?: boolean; pinned?: boolean }) =>
    request<AdminAnnouncement>('/admin/announcements', { method: 'POST', headers: bearer(token), body: JSON.stringify(payload) }),
  updateAnnouncement: (token: string, id: string, payload: { title?: string; content?: string; published?: boolean; pinned?: boolean }) =>
    request<AdminAnnouncement>(`/admin/announcements/${id}`, { method: 'PATCH', headers: bearer(token), body: JSON.stringify(payload) }),
  deleteAnnouncement: (token: string, id: string) =>
    request<{ id: string; status: string }>(`/admin/announcements/${id}`, { method: 'DELETE', headers: bearer(token) }),
  // —— 昵称 / 头像人工审核 ——
  profileReviews: (token: string, status = 'PENDING') =>
    request<ProfileReview[]>(`/admin/profile-reviews?status=${encodeURIComponent(status)}`, { headers: bearer(token) }),
  reviewProfile: (token: string, id: string, status: 'APPROVED' | 'REJECTED', reason?: string) =>
    request<ProfileReview>(`/admin/profile-reviews/${id}/${status}`, {
      method: 'PATCH',
      headers: bearer(token),
      body: JSON.stringify({ reason: reason ?? '' })
    }),
  approveAllProfileReviews: (token: string) =>
    request<{ approved: number; pending: number }>('/admin/profile-reviews/approve-all', {
      method: 'POST',
      headers: bearer(token)
    }),
  /**
   * 重置某个账号的违规资料：昵称换成中性占位、清掉自定义头像。
   * 已经生效的违规昵称/头像没有待审记录可驳回，只能走这里清理。
   */
  resetUserProfile: (token: string, userId: string, reason?: string) =>
    request<{ userId: string; nickname: string; reason: string }>(`/admin/users/${userId}/reset-profile`, {
      method: 'POST',
      headers: bearer(token),
      body: JSON.stringify({ reason: reason ?? '' })
    }),
  /**
   * 待审头像还在服务端暂存区，不是公开文件，所以必须带令牌取回再转成 blob URL。
   * 返回的 URL 用完要 revoke，避免内存泄漏。
   */
  profileReviewAvatar: async (token: string, id: string): Promise<string> => {
    if (DEMO_MODE) throw new Error('demo mode')
    const response = await fetch(`${API_ROOT}/admin/profile-reviews/${id}/avatar`, { headers: bearer(token) })
    if (!response.ok) throw new Error(`头像读取失败 (HTTP ${response.status})`)
    return URL.createObjectURL(await response.blob())
  }
}
